import React, { useState, useEffect, useRef } from 'react';
import Home from './market home';
import './market.css';
import axios from 'axios';
import Header from '../../components/header';
import { useSelector } from 'react-redux';
import { messengerService } from '../../functions/messenger';

const API_URL = process.env.REACT_APP_API_URL || "";

// Country codes with per-country phone number validation rules.
// `min` / `max` are the expected number of digits (excluding the country code).
const COUNTRY_CODES = [
  { code: '+91', flag: '🇮🇳', name: 'India',          min: 10, max: 10 },
  { code: '+1',  flag: '🇺🇸', name: 'USA / Canada',   min: 10, max: 10 },
  { code: '+44', flag: '🇬🇧', name: 'United Kingdom', min: 10, max: 10 },
  { code: '+86', flag: '🇨🇳', name: 'China',          min: 11, max: 11 },
  { code: '+81', flag: '🇯🇵', name: 'Japan',          min: 10, max: 11 },
  { code: '+49', flag: '🇩🇪', name: 'Germany',        min: 10, max: 11 },
  { code: '+33', flag: '🇫🇷', name: 'France',         min: 9,  max: 9  },
  { code: '+39', flag: '🇮🇹', name: 'Italy',          min: 9,  max: 11 },
  { code: '+7',  flag: '🇷🇺', name: 'Russia',         min: 10, max: 10 },
  { code: '+55', flag: '🇧🇷', name: 'Brazil',         min: 10, max: 11 },
  { code: '+61', flag: '🇦🇺', name: 'Australia',      min: 9,  max: 9  },
  { code: '+82', flag: '🇰🇷', name: 'South Korea',    min: 9,  max: 10 },
  { code: '+65', flag: '🇸🇬', name: 'Singapore',      min: 8,  max: 8  },
  { code: '+60', flag: '🇲🇾', name: 'Malaysia',       min: 9,  max: 10 },
  { code: '+66', flag: '🇹🇭', name: 'Thailand',       min: 9,  max: 9  },
  { code: '+84', flag: '🇻🇳', name: 'Vietnam',        min: 9,  max: 10 },
  { code: '+62', flag: '🇮🇩', name: 'Indonesia',      min: 9,  max: 12 },
  { code: '+63', flag: '🇵🇭', name: 'Philippines',    min: 10, max: 10 },
  { code: '+27', flag: '🇿🇦', name: 'South Africa',   min: 9,  max: 9  },
  { code: '+20', flag: '🇪🇬', name: 'Egypt',          min: 10, max: 10 },
];

const getCountryByCode = (code) =>
  COUNTRY_CODES.find((c) => c.code === code) || COUNTRY_CODES[0];

const getExpectedDigitsLabel = (country) => {
  if (!country) return 'a valid number of digits';
  return country.min === country.max
    ? `exactly ${country.min} digits`
    : `between ${country.min} and ${country.max} digits`;
};

const isContactValid = (contact, country) => {
  if (!country) return false;
  const len = (contact || '').length;
  return len >= country.min && len <= country.max;
};

// Add Material Icons CSS in the head
const iconStyle = document.createElement('link');
iconStyle.rel = 'stylesheet';
iconStyle.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
document.head.appendChild(iconStyle);

// Prevent Google Translate from translating Material Icons
// Add notranslate class to all Material Icons elements
let iconUpdateTimeout = null;
const addNotranslateToIcons = () => {
  try {
    const icons = document.querySelectorAll('.material-icons:not(.notranslate)');
    if (icons.length > 0) {
      icons.forEach(icon => {
        icon.classList.add('notranslate');
      });
    }
  } catch (error) {
    // Silently handle any errors to prevent ResizeObserver loops
    console.debug('Icon update skipped:', error);
  }
};

// Debounced version to prevent ResizeObserver loops
const debouncedAddNotranslate = () => {
  if (iconUpdateTimeout) {
    clearTimeout(iconUpdateTimeout);
  }
  iconUpdateTimeout = setTimeout(() => {
    requestAnimationFrame(() => {
      addNotranslateToIcons();
    });
  }, 100);
};

// Run immediately and after DOM updates
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(addNotranslateToIcons, 50);
  });
} else {
  setTimeout(addNotranslateToIcons, 50);
}

// Use MutationObserver with debouncing to catch icons added dynamically
let observer = null;
try {
  observer = new MutationObserver((mutations) => {
    // Only process if there are actual changes
    const hasIconChanges = mutations.some(mutation => {
      return Array.from(mutation.addedNodes).some(node => {
        if (node.nodeType === 1) { // Element node
          return node.classList?.contains('material-icons') || 
                 node.querySelector?.('.material-icons');
        }
        return false;
      });
    });
    
    if (hasIconChanges) {
      debouncedAddNotranslate();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
} catch (error) {
  console.debug('MutationObserver setup failed:', error);
}

// Suppress ResizeObserver errors (they're harmless warnings)
const originalError = console.error;
console.error = function(...args) {
  if (args[0]?.includes?.('ResizeObserver loop')) {
    return; // Suppress ResizeObserver loop errors
  }
  originalError.apply(console, args);
};

// Helper function to format contact number for display
const formatContactNumber = (contact) => {
  if (!contact) return 'Not provided';

  // Remove all non-digit characters for processing
  const digits = contact.replace(/\D/g, '');

  // If it's a 10-digit number, format as Indian mobile number
  if (digits.length === 10) {
    return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  }

  // If it already includes country code
  if (digits.length === 12 && digits.startsWith('91')) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }

  // If it has 13 digits and starts with +91
  if (digits.length === 13 && contact.startsWith('+91')) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }

  // Return as-is if format is not recognized
  return contact;
};

// Build a dialable tel: href from a contact string
const getContactTelHref = (contact) => {
  if (!contact) return null;
  const digits = String(contact).replace(/\D/g, '');
  if (!digits) return null;
  if (digits.length === 10) return `tel:+91${digits}`;
  if (digits.length === 12 && digits.startsWith('91')) return `tel:+${digits}`;
  if (String(contact).trim().startsWith('+')) return `tel:+${digits}`;
  return `tel:${digits}`;
};

// Helper function to check if product is new (less than 7 days old)
const isNewProduct = (createdAt) => {
  if (!createdAt) return false;
  const createdDate = new Date(createdAt);
  const now = new Date();
  const diffTime = Math.abs(now - createdDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= 7;
};

// Helper function to get appropriate icon for subcategories
const getSubcategoryIcon = (subcategoryName) => {
  const name = subcategoryName.toLowerCase();

  if (name.includes('mobile') || name.includes('phone') || name.includes('iphone') || name.includes('android')) {
    return 'smartphone';
  } else if (name.includes('laptop') || name.includes('computer')) {
    return 'laptop';
  } else if (name.includes('tv') || name.includes('television')) {
    return 'tv';
  } else if (name.includes('washing') || name.includes('machine')) {
    return 'local_laundry_service';
  } else if (name.includes('refrigerator') || name.includes('fridge')) {
    return 'kitchen';
  } else if (name.includes('car') || name.includes('vehicle')) {
    return 'directions_car';
  } else if (name.includes('bike') || name.includes('motorcycle')) {
    return 'motorcycle';
  } else if (name.includes('furniture') || name.includes('chair') || name.includes('table')) {
    return 'chair';
  } else if (name.includes('book') || name.includes('education')) {
    return 'book';
  } else if (name.includes('cloth') || name.includes('fashion') || name.includes('dress')) {
    return 'checkroom';
  } else if (name.includes('home') || name.includes('house')) {
    return 'home';
  } else if (name.includes('sport')) {
    return 'sports_basketball';
  } else if (name.includes('electronic') || name.includes('gadget')) {
    return 'devices';
  } else if (name.includes('beauty') || name.includes('cosmetic')) {
    return 'face';
  } else if (name.includes('food') || name.includes('kitchen')) {
    return 'restaurant';
  } else {
    return 'category';
  }
};

const IMAGE_MIN_SCALE = 1;
const IMAGE_MAX_SCALE = 4;
const IMAGE_DOUBLE_TAP_SCALE = 2.5;
const IMAGE_SWIPE_THRESHOLD = 50;
const IMAGE_DOUBLE_TAP_MS = 280;

const getTouchDistance = (t1, t2) =>
  Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// Mobile-friendly product image viewer: pinch zoom, double-tap zoom, pan, swipe
const ZoomableImageViewer = ({
  src,
  alt,
  imageKey,
  onSwipeLeft,
  onSwipeRight,
  canSwipe = true,
  children
}) => {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(IMAGE_MIN_SCALE);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [gestureActive, setGestureActive] = useState(false);

  const scaleRef = useRef(IMAGE_MIN_SCALE);
  const translateRef = useRef({ x: 0, y: 0 });
  const pinchStartDistanceRef = useRef(0);
  const pinchStartScaleRef = useRef(IMAGE_MIN_SCALE);
  const isPinchingRef = useRef(false);
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
  const swipeStartRef = useRef(null);
  const lastTapRef = useRef({ time: 0, x: 0, y: 0 });
  const suppressClickRef = useRef(false);
  const propsRef = useRef({ canSwipe, onSwipeLeft, onSwipeRight });
  const touchHandlersRef = useRef({
    onStart: () => {},
    onMove: () => {},
    onEnd: () => {}
  });

  propsRef.current = { canSwipe, onSwipeLeft, onSwipeRight };

  const resetZoom = () => {
    scaleRef.current = IMAGE_MIN_SCALE;
    translateRef.current = { x: 0, y: 0 };
    setScale(IMAGE_MIN_SCALE);
    setTranslate({ x: 0, y: 0 });
    setGestureActive(false);
    isPinchingRef.current = false;
    isPanningRef.current = false;
    swipeStartRef.current = null;
  };

  useEffect(() => {
    resetZoom();
  }, [src, imageKey]);

  const getClampBounds = (nextScale) => {
    const container = containerRef.current;
    if (!container || nextScale <= IMAGE_MIN_SCALE) {
      return { maxX: 0, maxY: 0 };
    }
    const { width, height } = container.getBoundingClientRect();
    return {
      maxX: ((nextScale - 1) * width) / 2,
      maxY: ((nextScale - 1) * height) / 2
    };
  };

  const applyTransform = (nextScale, nextTranslate) => {
    const { maxX, maxY } = getClampBounds(nextScale);
    const clamped = {
      x: clamp(nextTranslate.x, -maxX, maxX),
      y: clamp(nextTranslate.y, -maxY, maxY)
    };
    scaleRef.current = nextScale;
    translateRef.current = clamped;
    setScale(nextScale);
    setTranslate(clamped);
  };

  touchHandlersRef.current.onStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      isPinchingRef.current = true;
      isPanningRef.current = false;
      setGestureActive(true);
      swipeStartRef.current = null;
      pinchStartDistanceRef.current = getTouchDistance(e.touches[0], e.touches[1]);
      pinchStartScaleRef.current = scaleRef.current;
      return;
    }

    if (e.touches.length === 1) {
      const touch = e.touches[0];
      const now = Date.now();
      const lastTap = lastTapRef.current;
      const isDoubleTap =
        now - lastTap.time < IMAGE_DOUBLE_TAP_MS &&
        Math.hypot(touch.clientX - lastTap.x, touch.clientY - lastTap.y) < 40;

      if (isDoubleTap) {
        e.preventDefault();
        suppressClickRef.current = true;
        setGestureActive(false);
        lastTapRef.current = { time: 0, x: 0, y: 0 };

        if (scaleRef.current > IMAGE_MIN_SCALE) {
          applyTransform(IMAGE_MIN_SCALE, { x: 0, y: 0 });
        } else {
          const container = containerRef.current;
          if (container) {
            const rect = container.getBoundingClientRect();
            const offsetX = touch.clientX - (rect.left + rect.width / 2);
            const offsetY = touch.clientY - (rect.top + rect.height / 2);
            const nextScale = IMAGE_DOUBLE_TAP_SCALE;
            applyTransform(nextScale, {
              x: -offsetX * (nextScale - 1),
              y: -offsetY * (nextScale - 1)
            });
          } else {
            applyTransform(IMAGE_DOUBLE_TAP_SCALE, { x: 0, y: 0 });
          }
        }
        return;
      }

      lastTapRef.current = { time: now, x: touch.clientX, y: touch.clientY };

      if (scaleRef.current > IMAGE_MIN_SCALE) {
        isPanningRef.current = true;
        setGestureActive(true);
        panStartRef.current = {
          x: touch.clientX,
          y: touch.clientY,
          tx: translateRef.current.x,
          ty: translateRef.current.y
        };
        swipeStartRef.current = null;
      } else if (propsRef.current.canSwipe) {
        swipeStartRef.current = {
          x: touch.clientX,
          y: touch.clientY,
          time: now
        };
      }
    }
  };

  touchHandlersRef.current.onMove = (e) => {
    if (e.touches.length === 2 && isPinchingRef.current) {
      e.preventDefault();
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      if (!pinchStartDistanceRef.current) return;
      const ratio = distance / pinchStartDistanceRef.current;
      const nextScale = clamp(
        pinchStartScaleRef.current * ratio,
        IMAGE_MIN_SCALE,
        IMAGE_MAX_SCALE
      );
      applyTransform(nextScale, translateRef.current);
      return;
    }

    if (e.touches.length === 1 && isPanningRef.current && scaleRef.current > IMAGE_MIN_SCALE) {
      e.preventDefault();
      const touch = e.touches[0];
      const dx = touch.clientX - panStartRef.current.x;
      const dy = touch.clientY - panStartRef.current.y;
      applyTransform(scaleRef.current, {
        x: panStartRef.current.tx + dx,
        y: panStartRef.current.ty + dy
      });
    }
  };

  touchHandlersRef.current.onEnd = (e) => {
    if (isPinchingRef.current && e.touches.length < 2) {
      isPinchingRef.current = false;
      if (scaleRef.current <= IMAGE_MIN_SCALE + 0.05) {
        applyTransform(IMAGE_MIN_SCALE, { x: 0, y: 0 });
      }
    }

    if (e.touches.length === 0) {
      isPanningRef.current = false;
      setGestureActive(false);

      const { canSwipe: swipeEnabled, onSwipeLeft: goLeft, onSwipeRight: goRight } = propsRef.current;

      if (
        swipeStartRef.current &&
        scaleRef.current <= IMAGE_MIN_SCALE &&
        swipeEnabled
      ) {
        const start = swipeStartRef.current;
        const touch = e.changedTouches[0];
        const dx = touch.clientX - start.x;
        const dy = touch.clientY - start.y;
        const elapsed = Date.now() - start.time;

        if (
          Math.abs(dx) > IMAGE_SWIPE_THRESHOLD &&
          Math.abs(dx) > Math.abs(dy) * 1.2 &&
          elapsed < 600
        ) {
          if (dx < 0 && goLeft) goLeft();
          if (dx > 0 && goRight) goRight();
        }
      }

      swipeStartRef.current = null;
    }
  };

  // Non-passive listeners so preventDefault works for pinch/pan on mobile browsers
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const onStart = (e) => touchHandlersRef.current.onStart(e);
    const onMove = (e) => touchHandlersRef.current.onMove(e);
    const onEnd = (e) => touchHandlersRef.current.onEnd(e);

    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: false });
    el.addEventListener('touchcancel', onEnd, { passive: false });

    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  const isZoomed = scale > IMAGE_MIN_SCALE + 0.01;

  return (
    <div
      ref={containerRef}
      className={`image-modal-image-container ${isZoomed ? 'is-zoomed' : ''}`}
      onClick={(e) => {
        if (suppressClickRef.current) {
          e.stopPropagation();
          suppressClickRef.current = false;
        }
      }}
    >
      <div
        className="image-modal-zoom-layer"
        style={{
          transform: `translate3d(${translate.x}px, ${translate.y}px, 0) scale(${scale})`,
          transition: gestureActive ? 'none' : 'transform 0.2s ease-out'
        }}
      >
        <img
          src={src}
          alt={alt}
          className="image-modal-image"
          draggable={false}
        />
      </div>
      {/* Keep nav controls above the zoom layer */}
      {typeof children === 'function' ? children({ isZoomed }) : children}
    </div>
  );
};

const ProductCard = ({ product, onDelete, category, subCategory, index, currentUser, onStartConversation, onMarkAsSold, onImageClick, onEditSuccess }) => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [isMarked] = useState(false);
  const [showInterested, setShowInterested] = useState(false);
  const [interestCount, setInterestCount] = useState(product.interests?.length || 0);
  const [hasShownInterest, setHasShownInterest] = useState(
    product.interests?.some(interest => interest?.user?._id === currentUser?.id) || false
  );
  const [editMode, setEditMode] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [editFields, setEditFields] = useState({
    name: product.name || '',
    price: product.price || 0,
    description: product.description || '',
    contact: product.contact || ''
  });

  useEffect(() => {
    // Sync local edit fields if product prop changes (and not currently editing)
    if (!editMode) {
      setEditFields({
        name: product.name || '',
        price: product.price || 0,
        description: product.description || '',
        contact: product.contact || ''
      });
    }
  }, [product.name, product.price, product.description, product.contact, editMode]);

  const handleEditChange = (field, value) => {
    setEditFields(prev => ({ ...prev, [field]: value }));
  };

  const hasUnsavedChanges = () => {
    return (
      String(editFields.name || '').trim() !== String(product.name || '').trim() ||
      String(editFields.price) !== String(product.price ?? '') ||
      String(editFields.description || '').trim() !== String(product.description || '').trim() ||
      String(editFields.contact || '').trim() !== String(product.contact || '').trim()
    );
  };

  const validateEditFields = () => {
    const name = String(editFields.name || '').trim();
    const description = String(editFields.description || '').trim();
    const contact = String(editFields.contact || '').trim();
    const price = Number(editFields.price);

    if (!name) {
      alert('Product name is required');
      return false;
    }
    if (editFields.price === '' || editFields.price === null || editFields.price === undefined || Number.isNaN(price)) {
      alert('Price is required');
      return false;
    }
    if (price < 0) {
      alert('Price cannot be negative');
      return false;
    }
    if (!description) {
      alert('Description is required');
      return false;
    }
    if (!contact) {
      alert('Contact is required');
      return false;
    }
    return true;
  };

  const discardEdit = () => {
    setEditFields({
      name: product.name || '',
      price: product.price || 0,
      description: product.description || '',
      contact: product.contact || ''
    });
    setEditMode(false);
    setShowUnsavedDialog(false);
    setShowMenu(false);
  };

  const requestExitEditMode = () => {
    if (hasUnsavedChanges()) {
      setShowMenu(false);
      setShowUnsavedDialog(true);
      return;
    }
    discardEdit();
  };

  const handleSaveEdit = async (e) => {
    if (e?.stopPropagation) e.stopPropagation();
    if (!currentUser?.token) {
      alert('Please log in to edit your product');
      return false;
    }
    if (!validateEditFields()) {
      return false;
    }
    if (isSaving) return false;

    setIsSaving(true);
    try {
      const payload = {
        name: String(editFields.name).trim(),
        price: Number(editFields.price),
        description: String(editFields.description).trim(),
        contact: String(editFields.contact).trim(),
      };
      const res = await axios.put(`${API_URL}/api/products/${product._id}`, payload, {
        headers: { 'Authorization': `Bearer ${currentUser.token}` }
      });
      // Update local fields from response if available
      const updated = res.data?.product || payload;
      setEditFields({
        name: updated.name ?? payload.name,
        price: updated.price ?? payload.price,
        description: updated.description ?? payload.description,
        contact: updated.contact ?? payload.contact,
      });
      // Mutate product prop to reflect new values in UI immediately
      product.name = updated.name ?? payload.name;
      product.price = updated.price ?? payload.price;
      product.description = updated.description ?? payload.description;
      product.contact = updated.contact ?? payload.contact;

      setEditMode(false);
      setShowUnsavedDialog(false);
      setShowMenu(false);
      if (onEditSuccess) {
        onEditSuccess('Product updated successfully');
      } else {
        alert('Product updated successfully');
      }
      return true;
    } catch (error) {
      console.error('Error updating product:', error);
      alert(error.response?.data?.error || 'Failed to update product');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const images = product.images || [product.image];

  useEffect(() => {
    let interval;
    if (isHovering && images.length > 1) {
      interval = setInterval(() => {
        setCurrentImage((prev) => (prev + 1) % images.length);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isHovering, images.length]);

  useEffect(() => {
    // Count at most one view per logged-in user (backend dedupes repeats)
    if (product._id && currentUser?.token) {
      axios.post(
        `${API_URL}/api/products/${product._id}/view`,
        {},
        { headers: { Authorization: `Bearer ${currentUser.token}` } }
      ).catch(error => console.error('Error tracking view:', error));
    }
  }, [product._id, currentUser?.token]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenu) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showMenu]);




  const handleInterestToggle = async (e) => {
    e.stopPropagation();
    if (!currentUser) {
      alert('Please log in to show interest');
      return;
    }

    try {
      if (hasShownInterest) {
        // Remove interest
        await axios.delete(`${API_URL}/api/products/${product._id}/interest`, {
          headers: { 'Authorization': `Bearer ${currentUser.token}` }
        });
        setHasShownInterest(false);
        setInterestCount(prev => prev - 1);
      } else {
        // Show interest
        await axios.post(`${API_URL}/api/products/${product._id}/interest`, {}, {
          headers: { 'Authorization': `Bearer ${currentUser.token}` }
        });
        setHasShownInterest(true);
        setInterestCount(prev => prev + 1);
        setShowInterested(true);
        setTimeout(() => setShowInterested(false), 2000);
      }
    } catch (error) {
      console.error('Error handling interest:', error);
      alert(error.response?.data?.error || 'Failed to update interest');
    }
  };

  const handleReadMoreToggle = (e) => {
    e.stopPropagation();
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };



  // Check if current user is the uploader of this product
  const isOwner = currentUser && product.uploader && (product.uploader._id === currentUser.id);

  // Values to show depending on edit mode
  const nameToShow = editMode ? editFields.name : product.name;
  const priceToShow = editMode ? editFields.price : product.price;
  const descriptionToShow = editMode ? editFields.description : product.description;
  const contactToShow = editMode ? editFields.contact : product.contact;
  const contactTelHref = getContactTelHref(contactToShow);
  const canViewContact = hasShownInterest || isOwner;

  return (
    <div className={`product-card ${editMode ? 'edit-mode' : ''} ${product.featured ? 'featured' : ''} ${isNewProduct(product.createdAt) ? 'new-product' : ''}`}>
      <div className="product-image"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {/* Single prominent SOLD badge — shown for any sold product, in any view */}
        {product.status === 'sold' && (
          <div
            className="sold-badge-on-image"
            aria-label="Product sold"
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
              color: 'white',
              padding: '6px 14px',
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.5px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 2px 10px rgba(40, 167, 69, 0.35)',
              zIndex: 3,
              pointerEvents: 'none',
            }}
          >
            <span aria-hidden="true" style={{ fontSize: '14px', lineHeight: 1 }}>✓</span>
            SOLD
          </div>
        )}
        {isMarked && (
          <div className="mark-overlay">
            <span className="checkmark">✓</span>
          </div>
        )}
        {showInterested && (
          <div className="interest-overlay">
            <span className="interest-message">Interest shown! ❤️</span>
          </div>
        )}
        <img
          src={images[currentImage]}
          alt={`${product.name}-${currentImage}`}
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            onImageClick && onImageClick(images, currentImage, product.name);
          }}
        />
        {images.length > 1 && (
          <div className="product-dots">
            {images.map((_, idx) => (
              <div
                key={idx}
                className={`product-dot ${idx === currentImage ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentImage(idx);
                }}
                style={{ cursor: 'pointer' }}
              />
            ))}
          </div>
        )}
      </div>

      <div className="product-info" style={{ position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
          <h3 className="product-title" style={{ margin: 0, flex: 1 }}>
            {editMode ? (
              <input
                type="text"
                value={editFields.name}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => handleEditChange('name', e.target.value)}
                className="form-input"
                placeholder="Product name"
              />
            ) : (
              nameToShow
            )}
          </h3>

          {/* Three-dot menu — hidden while editing; Save/Cancel are shown below fields */}
          {!editMode && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.2s ease',
                marginLeft: '0.5rem'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }}
            >
              <span className="material-icons notranslate" style={{ color: 'white', fontSize: '18px' }}>more_vert</span>
            </button>
          )}
        </div>

        <div className="product-price">
          {editMode ? (
            <input
              type="number"
              min="0"
              value={editFields.price}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => handleEditChange('price', e.target.value)}
              className="form-input"
              placeholder="Price"
              style={{ width: '100px' }}
            />
          ) : (
            <>₹{priceToShow}</>
          )}
        </div>

        <p className="product-description">
          {editMode ? (
            <textarea
              value={editFields.description}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => handleEditChange('description', e.target.value)}
              className="form-input form-textarea"
              rows={3}
              placeholder="Description"
            />
          ) : (
            <>
              <span style={{
                display: isDescriptionExpanded ? 'block' : '-webkit-box',
                WebkitLineClamp: isDescriptionExpanded ? 'unset' : 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: '1.2',
                maxHeight: isDescriptionExpanded ? 'none' : '2.4em'
              }}>
                {descriptionToShow}
              </span>
              {descriptionToShow && descriptionToShow.length > 150 && (
                <button
                  onClick={handleReadMoreToggle}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#007bff',
                    cursor: 'pointer',
                    fontSize: '12px',
                    padding: '2px 0',
                    marginTop: '2px',
                    textDecoration: 'underline'
                  }}
                >
                  {isDescriptionExpanded ? 'Read less' : 'Read more'}
                </button>
              )}
            </>
          )}
        </p>

        <div className="product-contact">
          {canViewContact ? (
            editMode ? (
              <>
                <span className="material-icons notranslate">phone</span>
                <input
                  type="text"
                  value={editFields.contact}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => handleEditChange('contact', e.target.value)}
                  className="form-input"
                  placeholder="Contact"
                />
              </>
            ) : contactTelHref ? (
              <a
                href={contactTelHref}
                className="contact-call-link"
                onClick={(e) => e.stopPropagation()}
                title="Call seller"
              >
                <span className="material-icons notranslate">phone</span>
                <span>{formatContactNumber(contactToShow)}</span>
              </a>
            ) : (
              <>
                <span className="material-icons notranslate">phone</span>
                <span>{formatContactNumber(contactToShow)}</span>
              </>
            )
          ) : (
            <span className="contact-restricted">
              🔒 Show interest to view contact number.
            </span>
          )}
        </div>

        {/* Inline edit actions — shown below fields so user doesn't reopen the menu */}
        {editMode && (
          <div
            className="product-edit-actions"
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'flex',
              gap: '10px',
              marginTop: '12px',
              marginBottom: '4px'
            }}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                requestExitEditMode();
              }}
              disabled={isSaving}
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: '10px',
                border: '1px solid #d1d5db',
                background: '#ffffff',
                color: '#374151',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                opacity: isSaving ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                await handleSaveEdit(e);
              }}
              disabled={isSaving}
              style={{
                flex: 1,
                padding: '10px 12px',
                borderRadius: '10px',
                border: 'none',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#ffffff',
                cursor: isSaving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                opacity: isSaving ? 0.7 : 1,
                boxShadow: '0 2px 6px rgba(16, 185, 129, 0.35)'
              }}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}

        {/* Product Stats */}
        <div className="product-stats">
          <div className="product-stat">
            <span className="product-stat-icon material-icons notranslate">visibility</span>
            <span>{product.views || 0} views</span>
          </div>
          <div className="product-stat">
            <span className="product-stat-icon material-icons notranslate">favorite</span>
            <span>{interestCount} interests</span>
          </div>
        </div>

        {/* Uploader Information */}
        {product.uploader && (
          <div className="product-uploader">
            <img
              src={product.uploader.picture}
              alt={product.uploader.name}
              className="uploader-avatar"
            />
            <span className="uploader-name">
              {product.uploader.first_name} {product.uploader.last_name}
            </span>
          </div>
        )}

        {/* Dropdown Menu — hidden while editing */}
        {showMenu && !editMode && (
          <div style={{
            position: 'absolute',
            top: '40px',
            right: '0',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            minWidth: '200px',
            zIndex: 1000,
            overflow: 'hidden',
            animation: 'slideDown 0.2s ease-out'
          }}>
            {/* Only show interest button if user is not the owner */}
            {!isOwner && currentUser && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleInterestToggle(e);
                  setShowMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: hasShownInterest ? '#10b981' : '#3b82f6',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <span className="material-icons notranslate" style={{ fontSize: '18px' }}>
                  {hasShownInterest ? 'favorite' : 'favorite_border'}
                </span>
                {hasShownInterest ? 'Interested' : 'Show Interest'}
              </button>
            )}

            {/* Message seller button - only show if user has shown interest and it's not their product */}
            {!isOwner && currentUser && hasShownInterest && product.uploader && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStartConversation(product.uploader._id, product);
                  setShowMenu(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#6b7280',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <span className="material-icons notranslate" style={{ fontSize: '18px' }}>message</span>
                Message Seller
              </button>
            )}

            {/* Product owner actions (view mode only) */}
            {isOwner && (
              <>
                {product.status !== 'sold' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditMode(true);
                      setShowMenu(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#6b7280',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <span className="material-icons notranslate" style={{ fontSize: '18px' }}>edit</span>
                    Edit
                  </button>
                )}

                {product.status !== 'sold' && onMarkAsSold && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMarkAsSold(product._id);
                      setShowMenu(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#10b981',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <span className="material-icons notranslate" style={{ fontSize: '18px' }}>check_circle</span>
                    Mark as Sold
                  </button>
                )}

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(product._id, category, subCategory, index);
                    setShowMenu(false);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: 'none',
                    background: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#ef4444',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#fef2f2';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span className="material-icons notranslate" style={{ fontSize: '18px' }}>delete</span>
                  Delete Product
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Unsaved changes confirmation */}
      {showUnsavedDialog && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '16px'
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              padding: '24px',
              maxWidth: '400px',
              width: '100%',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)'
            }}
          >
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', color: '#111827' }}>
              Unsaved changes
            </h3>
            <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#4b5563', lineHeight: 1.5 }}>
              You have unsaved changes. Do you want to save or discard them?
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setShowUnsavedDialog(false)}
                disabled={isSaving}
                style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  background: '#fff',
                  color: '#374151',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Keep editing
              </button>
              <button
                type="button"
                onClick={discardEdit}
                disabled={isSaving}
                style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#f3f4f6',
                  color: '#374151',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Discard
              </button>
              <button
                type="button"
                onClick={async () => {
                  await handleSaveEdit();
                }}
                disabled={isSaving}
                style={{
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#10b981',
                  color: '#fff',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: isSaving ? 0.7 : 1
                }}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function App() {
  const { user } = useSelector((state) => ({ ...state }));
  const [activeCategory, setActiveCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [showAddProductForm, setShowAddProductForm] = useState(false);
  const [, setProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({
    name: '',
    category: '',
    subcategory: '',
    images: [],
    price: '',
    contact: '',
    countryCode: '+91',
    description: '',
    mobileSubCategory: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteInfo, setDeleteInfo] = useState({});
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  // Country code dropdown (custom searchable popover) state
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const countryDropdownRef = useRef(null);
  const countrySearchInputRef = useRef(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [allCategories, setAllCategories] = useState([]);
  const [showAddSubcategoryModal, setShowAddSubcategoryModal] = useState(false);
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [currentView, setCurrentView] = useState('marketplace'); // 'marketplace' or 'my-products'
  const [userProducts, setUserProducts] = useState([]);
  const [soldProducts, setSoldProducts] = useState([]);
  const [myProductsView, setMyProductsView] = useState('available'); // 'available' or 'sold'
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [modalCurrentIndex, setModalCurrentIndex] = useState(0);
  const [modalProductName, setModalProductName] = useState('');

  // Get authentication headers
  const getAuthHeaders = () => {
    return user?.token ? {
      'Authorization': `Bearer ${user.token}`
    } : {};
  };

  // Fetch all categories from database
  const fetchAllCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      const dbCategories = response.data.map(cat => ({
        id: cat.value,
        name: cat.name,
        value: cat.value,
        _id: cat._id,
        isDefault: cat.isDefault,
        createdBy: cat.createdBy,
        subcategories: cat.subcategories || []
      }));

      setAllCategories(dbCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setAllCategories([]);
    }
  };

  // Fetch products from database
  const fetchProducts = async (category = null, subcategory = null) => {
    try {
      let url = `${API_URL}/api/products`;
      const params = new URLSearchParams();

      if (category) {
        params.append('category', category);
      }
      if (subcategory) {
        params.append('subcategory', subcategory);
      }

      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      return [];
    }
  };

  // Fetch user's own products
  const fetchUserProducts = async (status = null) => {
    if (!user?.token) return [];

    try {
      let url = `${API_URL}/api/products/my-products`;
      if (status) {
        url += `?status=${status}`;
      }

      const response = await axios.get(url, {
        headers: getAuthHeaders()
      });
      return response.data.products || [];
    } catch (error) {
      console.error('Error fetching user products:', error);
      return [];
    }
  };

  // Handle image click to open modal
  const handleImageClick = (images, currentIndex, productName) => {
    setModalImages(images);
    setModalCurrentIndex(currentIndex);
    setModalProductName(productName);
    setShowImageModal(true);
  };

  // Close image modal
  const closeImageModal = () => {
    setShowImageModal(false);
    setModalImages([]);
    setModalCurrentIndex(0);
    setModalProductName('');
  };

  // Navigate between images in modal
  const nextModalImage = () => {
    setModalCurrentIndex((prev) => (prev + 1) % modalImages.length);
  };

  const prevModalImage = () => {
    setModalCurrentIndex((prev) => (prev - 1 + modalImages.length) % modalImages.length);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!showImageModal) return;

      if (e.key === 'Escape') {
        closeImageModal();
      } else if (e.key === 'ArrowRight') {
        nextModalImage();
      } else if (e.key === 'ArrowLeft') {
        prevModalImage();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showImageModal, modalImages.length]);

  // Close the country code popover on outside click / ESC, and focus the
  // search input when it opens so the user can start typing immediately.
  useEffect(() => {
    if (!countryDropdownOpen) return undefined;

    const handlePointer = (e) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(e.target)
      ) {
        setCountryDropdownOpen(false);
        setCountrySearch('');
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setCountryDropdownOpen(false);
        setCountrySearch('');
      }
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    document.addEventListener('keydown', handleKey);

    // Focus the search field shortly after the popover renders.
    const focusTimer = setTimeout(() => {
      countrySearchInputRef.current?.focus();
    }, 30);

    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
      document.removeEventListener('keydown', handleKey);
      clearTimeout(focusTimer);
    };
  }, [countryDropdownOpen]);

  // Mark product as sold
  const handleMarkAsSold = async (productId) => {
    try {
      await axios.put(
        `${API_URL}/api/products/${productId}/mark-sold`,
        {},
        { headers: getAuthHeaders() }
      );

      // Refresh current view
      if (currentView === 'my-products') {
        if (myProductsView === 'available') {
          fetchUserProducts('available').then(setUserProducts);
        } else {
          fetchUserProducts('sold').then(setSoldProducts);
        }
      }

      // Remove product from marketplace view if we're in marketplace
      if (currentView === 'marketplace') {
        if (activeCategory) {
          const subcategoryValue = selectedSubcategory?.value || selectedSubcategory?.name;
          fetchProducts(activeCategory, subcategoryValue).then(setFilteredProducts);
        } else {
          fetchProducts().then(setProducts);
        }
      }

      setSuccessMessage('Product marked as sold successfully!');
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 2000);
    } catch (error) {
      console.error('Error marking product as sold:', error);
      alert(error.response?.data?.error || 'Failed to mark product as sold');
    }
  };

  const handleEditSuccess = (message = 'Product updated successfully') => {
    setSuccessMessage(message);
    setShowSuccessPopup(true);
    setTimeout(() => {
      setShowSuccessPopup(false);
    }, 2000);
  };

  // Initialize categories and products when component mounts
  useEffect(() => {
    fetchAllCategories();
    fetchProducts().then(setProducts);
  }, []);

  // Update filtered products when category or subcategory changes
  useEffect(() => {
    if (activeCategory) {
      const subcategoryValue = selectedSubcategory?.value || selectedSubcategory?.name;
      fetchProducts(activeCategory, subcategoryValue).then(setFilteredProducts);
    }
  }, [activeCategory, selectedSubcategory]);

  // Load user products when switching to my-products view
  useEffect(() => {
    if (currentView === 'my-products' && user) {
      if (myProductsView === 'available') {
        fetchUserProducts('available').then(setUserProducts);
        setSoldProducts([]); // Clear sold products when viewing available
      } else {
        fetchUserProducts('sold').then(setSoldProducts);
        setUserProducts([]); // Clear available products when viewing sold
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, user, myProductsView]);

  // Add subcategory to current active category
  const handleAddSubcategory = async () => {
    if (newSubcategoryName.trim() && activeCategory) {
      try {
        const currentCategory = allCategories.find(cat => cat.id === activeCategory || cat.value === activeCategory);

        if (!currentCategory || !currentCategory._id) {
          alert('Category not found. Please refresh the page and try again.');
          return;
        }

        // Check for duplicate subcategory name
        const subcategoryExists = currentCategory.subcategories?.some(sub =>
          sub.name.toLowerCase() === newSubcategoryName.trim().toLowerCase()
        );

        if (subcategoryExists) {
          alert('Subcategory already exists in this category! Please choose a different name.');
          return;
        }

        const response = await axios.post(`${API_URL}/api/categories/${currentCategory._id}/subcategory`, {
          name: newSubcategoryName.trim()
        }, {
          headers: getAuthHeaders()
        });

        // Update local categories
        setAllCategories(prev => prev.map(cat =>
          cat._id === currentCategory._id
            ? { ...cat, subcategories: response.data.category.subcategories }
            : cat
        ));

        // Reset form
        setNewSubcategoryName('');
        setShowAddSubcategoryModal(false);

        alert('Subcategory added successfully!');

        // Refresh categories to show updated subcategories
        fetchAllCategories();
      } catch (error) {
        console.error('Error adding subcategory:', error);
        const errorMessage = error.response?.data?.error || 'Failed to add subcategory';
        if (errorMessage.includes('already exists')) {
          alert('Subcategory already exists in this category! Please choose a different name.');
        } else {
          alert(errorMessage);
        }
      }
    }
  };

  // Delete category function
  const handleDeleteCategory = async (categoryId) => {
    setCategoryToDelete(categoryId);
    setShowDeleteCategoryModal(true);
  };

  const confirmDeleteCategory = async () => {
    try {
      await axios.delete(`${API_URL}/api/categories/${categoryToDelete}`, {
        headers: getAuthHeaders()
      });

      // Refresh categories list
      fetchAllCategories();

      // If we're currently viewing the deleted category, go back to home
      if (activeCategory === categoryToDelete) {
        setActiveCategory(null);
        setSelectedSubcategory(null);
      }

      setShowDeleteCategoryModal(false);
      setCategoryToDelete(null);
      alert('Category deleted successfully!');
    } catch (error) {
      console.error('Error deleting category:', error);
      alert(error.response?.data?.error || 'Failed to delete category');
      setShowDeleteCategoryModal(false);
      setCategoryToDelete(null);
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const totalImages = newProduct.images.length + files.length;

    if (totalImages > 5) {
      alert('You can upload maximum 5 images per product');
      return;
    }

    setNewProduct(prev => ({
      ...prev,
      images: [...prev.images, ...files].slice(0, 5)
    }));
  };

  const handleAddProduct = async () => {
    const { name, category, price, contact, countryCode, images, description, mobileSubCategory } = newProduct;

    // Check required fields and image count
    if (!name || !category || !price || !contact || !description || images.length < 3 || images.length > 5) {
      let message = 'Please fix the following issues:\n';
      if (!name) message += '• Product Name is required\n';
      if (!category) message += '• Category is required\n';
      if (!price) message += '• Price is required\n';
      if (!contact) message += '• Contact is required\n';
      if (!description) message += '• Description is required\n';
      if (images.length < 3) message += '• Minimum 3 images are required\n';
      if (images.length > 5) message += '• Maximum 5 images are allowed\n';
      setValidationMessage(message);
      setShowValidationModal(true);
      return;
    }

    // Validate contact number against the selected country's digit rules.
    const selectedCountry = getCountryByCode(countryCode);
    if (!isContactValid(contact, selectedCountry)) {
      setValidationMessage(
        `Contact number must be ${getExpectedDigitsLabel(selectedCountry)} for ${selectedCountry.name} (${selectedCountry.code})`
      );
      setShowValidationModal(true);
      return;
    }

    const formData = new FormData();
    formData.append('name', name);
    formData.append('category', category);
    formData.append('price', Number(price));
    formData.append('contact', `${countryCode}${contact}`);
    formData.append('description', description);

    if (category === 'mobiles' && mobileSubCategory) {
      formData.append('mobileSubCategory', mobileSubCategory);
    }

    // Handle general subcategories
    if (newProduct.subcategory) {
      formData.append('subcategory', newProduct.subcategory);
    }

    // Handle images properly
    images.forEach((img, index) => {
      if (img instanceof File) {
        formData.append('images', img);
      }
      else if (img.startsWith('data:image')) {
        const byteString = atob(img.split(',')[1]);
        const mimeString = img.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: mimeString });
        const file = new File([blob], `image-${index}.${mimeString.split('/')[1]}`, { type: mimeString });
        formData.append('images', file);
      }
      else if (typeof img === 'string') {
        formData.append('existingImages', img);
      }
    });

    try {
      await axios.post(`${API_URL}/api/products/add`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...getAuthHeaders()
        }
      });

      setShowAddProductForm(false);
      setNewProduct({ name: '', category: '', subcategory: '', images: [], price: '', contact: '', countryCode: '+91', description: '', mobileSubCategory: '' });

      // Refresh products list
      const allProducts = await fetchProducts();
      setProducts(allProducts);

      // Refresh filtered products if viewing a category
      if (activeCategory) {
        const subcategoryValue = selectedSubcategory?.value || selectedSubcategory?.name;
        const updatedFilteredProducts = await fetchProducts(activeCategory, subcategoryValue);
        setFilteredProducts(updatedFilteredProducts);
      }

      // Refresh user products if in my-products view
      if (currentView === 'my-products') {
        const updatedUserProducts = await fetchUserProducts();
        setUserProducts(updatedUserProducts);
      }

      setSuccessMessage('Product added successfully');
      setShowSuccessPopup(true);
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 2000);
    } catch (error) {
      alert('Failed to add product: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteProduct = async (productId, category, subCategory, index) => {
    setDeleteInfo({ productId, category, subCategory, index });
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (deleteInfo) {
      try {
        const headers = getAuthHeaders();
        await axios.delete(`${API_URL}/api/products/${deleteInfo.productId}`, { headers });

        // Remove product from current view lists
        if (currentView === 'my-products') {
          setUserProducts(prev => prev.filter(p => p._id !== deleteInfo.productId));
          setSoldProducts(prev => prev.filter(p => p._id !== deleteInfo.productId));
        } else {
          setProducts(prev => prev.filter((p, i) =>
            !(i === deleteInfo.index &&
              p.category === deleteInfo.category &&
              p.subCategory === deleteInfo.subCategory)
          ));
          setFilteredProducts(prev => prev.filter(p => p._id !== deleteInfo.productId));
        }

        setShowDeleteModal(false);
        setDeleteInfo(null);
        setSuccessMessage('Product deleted successfully!');
        setShowSuccessPopup(true);
        setTimeout(() => {
          setShowSuccessPopup(false);
        }, 2000);
      } catch (error) {
        console.error('Error deleting product:', error);
        setShowDeleteModal(false);
        alert('Failed to delete product: ' + (error.response?.data?.error || 'Unknown error'));
      }
    }
  };

  // Function to start conversation about a specific product
  const startConversationWithProductContext = async (otherUserId, product) => {
    if (!user?.token) {
      alert('Please log in to start a conversation');
      return;
    }

    if (!otherUserId) {
      alert('Unable to start conversation: User information not available');
      return;
    }

    try {
      // Create or get conversation with the other user
      const conversation = await messengerService.getOrCreateConversation({
        participantId: otherUserId,
        type: 'private'
      }, user.token);

      // Determine the message based on who is initiating
      let messageText;
      if (user.id === product.uploader?._id) {
        // Product owner messaging interested user
        messageText = `Hi! I saw you're interested in my product "${product.name}" (₹${product.price}). Would you like to know more about it?`;
      } else {
        // Interested user messaging product owner
        messageText = `Hi! I'm interested in your product "${product.name}" (₹${product.price}). Could you tell me more about it?`;
      }

      // Send an initial message about the product
      const productMessage = {
        type: 'text',
        content: {
          text: messageText
        }
      };

      await messengerService.sendMessage({
        ...productMessage,
        conversationId: conversation._id,
      }, user.token);

      // Open messenger sidebar
      window.dispatchEvent(new CustomEvent('toggleMessenger'));

    } catch (error) {
      console.error('Error starting conversation:', error);
      alert('Failed to start conversation: ' + (error.response?.data?.message || 'Unknown error'));
    }
  };

  return (
    <div className="App">
      {showValidationModal && (
        <div className="delete-modal-overlay">
          <div className="delete-modal" style={{
            minWidth: '350px',
            background: 'white',
            borderRadius: '10px',
            padding: '20px'
          }}>
            <h3 style={{
              color: '#e74c3c',
              textAlign: 'center',
              fontSize: '22px',
              marginBottom: '20px'
            }}>Please Fill Required Fields</h3>
            <div style={{
              padding: '10px 20px'
            }}>
              {validationMessage.split('\n').filter(line => line.trim()).map((line, index) => (
                line.includes('fields:') ? (
                  <p key={index} style={{ fontWeight: 'bold', marginBottom: '10px' }}>{line}</p>
                ) : (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    margin: '8px 0',
                    color: '#e74c3c',
                    fontSize: '16px'
                  }}>
                    <span style={{ marginRight: '10px', fontSize: '20px' }}>•</span>
                    {line.replace('•', '')}
                  </div>
                )
              ))}
            </div>
            <div className="delete-modal-buttons">
              <button
                className="delete-modal-cancel"
                onClick={() => setShowValidationModal(false)}
                style={{
                  background: '#25b5c0e6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 30px',
                  borderRadius: '5px',
                  fontSize: '16px',
                  cursor: 'pointer'
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>)}
      {showSuccessPopup && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          zIndex: 1000,
          minWidth: '300px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            backgroundColor: '#00A652',
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <span className="material-icons notranslate" style={{
              color: 'white',
              fontSize: '45px'
            }}>check</span>
          </div>
          <p style={{
            margin: 0,
            fontSize: '20px',
            color: '#333333',
            fontWeight: '400'
          }}>{successMessage}</p>
        </div>
      )}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            position: 'relative',
            animation: 'slideIn 0.4s ease-out',
            backdropFilter: 'blur(10px)',
            transform: 'translateY(0)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            maxWidth: '450px',
            width: '90vw',
            overflow: 'hidden'
          }}>
            {/* Decorative background elements */}
            <div style={{
              position: 'absolute',
              top: '-2px',
              left: '-2px',
              right: '-2px',
              bottom: '-2px',
              background: 'linear-gradient(45deg, #ef4444 0%, #dc2626 100%)',
              borderRadius: '22px',
              zIndex: -1,
              opacity: 0.1
            }}></div>

            {/* Header with warning icon */}
            <div style={{
              background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
              padding: '2rem 2rem 1.5rem 2rem',
              borderBottom: '2px solid rgba(239, 68, 68, 0.1)',
              textAlign: 'center',
              position: 'relative'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem auto',
                boxShadow: '0 8px 16px rgba(239, 68, 68, 0.3)'
              }}>
                <span className="material-icons notranslate" style={{
                  fontSize: '2rem',
                  color: 'white',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
                }}>delete_forever</span>
              </div>

              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '800',
                color: '#991b1b',
                margin: 0,
                letterSpacing: '-0.025em',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
              }}>
                Delete Product
              </h3>
            </div>

            {/* Content */}
            <div style={{
              padding: '2rem',
              textAlign: 'center'
            }}>
              <p style={{
                fontSize: '1.1rem',
                color: '#374151',
                margin: '0 0 2rem 0',
                lineHeight: '1.6',
                fontWeight: '500'
              }}>
                Are you sure you want to delete this product?
              </p>
              <p style={{
                fontSize: '1rem',
                color: '#6b7280',
                margin: 0,
                lineHeight: '1.5',
                fontWeight: '400'
              }}>
                This action cannot be undone.
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              padding: '0 2rem 2rem 2rem',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  padding: '1rem 2rem',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                  color: '#64748b',
                  border: '2px solid rgba(99, 102, 241, 0.1)',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '700',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  minWidth: '120px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)';
                  e.target.style.color = '#475569';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 15px -3px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)';
                  e.target.style.color = '#64748b';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
              >
                <span className="material-icons notranslate" style={{ fontSize: '1.25rem' }}>close</span>
                Cancel
              </button>

              <button
                onClick={confirmDelete}
                style={{
                  padding: '1rem 2rem',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '700',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  boxShadow: '0 8px 15px -3px rgba(239, 68, 68, 0.4)',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                  minWidth: '120px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)';
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.boxShadow = '0 12px 20px -5px rgba(239, 68, 68, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 15px -3px rgba(239, 68, 68, 0.4)';
                }}
              >
                <span className="material-icons notranslate" style={{
                  fontSize: '1.25rem',
                  filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))'
                }}>delete_forever</span>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Category Modal */}
      {showDeleteCategoryModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            position: 'relative',
            animation: 'slideIn 0.4s ease-out',
            backdropFilter: 'blur(10px)',
            transform: 'translateY(0)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            maxWidth: '450px',
            width: '90vw',
            overflow: 'hidden'
          }}>
            {/* Decorative background elements */}
            <div style={{
              position: 'absolute',
              top: '-2px',
              left: '-2px',
              right: '-2px',
              bottom: '-2px',
              background: 'linear-gradient(45deg, #ef4444 0%, #dc2626 100%)',
              borderRadius: '22px',
              zIndex: -1,
              opacity: 0.1
            }}></div>

            {/* Header with warning icon */}
            <div style={{
              background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
              padding: '2rem 2rem 1.5rem 2rem',
              borderBottom: '2px solid rgba(239, 68, 68, 0.1)',
              textAlign: 'center',
              position: 'relative'
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem auto',
                boxShadow: '0 8px 16px rgba(239, 68, 68, 0.3)'
              }}>
                <span className="material-icons notranslate" style={{
                  fontSize: '2rem',
                  color: 'white',
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
                }}>warning</span>
              </div>

              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '800',
                color: '#991b1b',
                margin: 0,
                letterSpacing: '-0.025em',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
              }}>
                Delete Category
              </h3>
            </div>

            {/* Content */}
            <div style={{
              padding: '2rem',
              textAlign: 'center'
            }}>
              <p style={{
                fontSize: '1.1rem',
                color: '#374151',
                margin: '0 0 2rem 0',
                lineHeight: '1.6',
                fontWeight: '500'
              }}>
                Are you sure you want to delete this category?
              </p>
              <p style={{
                fontSize: '1rem',
                color: '#6b7280',
                margin: 0,
                lineHeight: '1.5',
                fontWeight: '400'
              }}>
                This action cannot be undone.
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              padding: '0 2rem 2rem 2rem',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => setShowDeleteCategoryModal(false)}
                style={{
                  padding: '1rem 2rem',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                  color: '#64748b',
                  border: '2px solid rgba(99, 102, 241, 0.1)',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '700',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  minWidth: '120px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)';
                  e.target.style.color = '#475569';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 8px 15px -3px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)';
                  e.target.style.color = '#64748b';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                }}
              >
                <span className="material-icons notranslate" style={{ fontSize: '1.25rem' }}>close</span>
                Cancel
              </button>

              <button
                onClick={confirmDeleteCategory}
                style={{
                  padding: '1rem 2rem',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: '700',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  boxShadow: '0 8px 15px -3px rgba(239, 68, 68, 0.4)',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                  minWidth: '120px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)';
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.boxShadow = '0 12px 20px -5px rgba(239, 68, 68, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 15px -3px rgba(239, 68, 68, 0.4)';
                }}
              >
                <span className="material-icons notranslate" style={{
                  fontSize: '1.25rem',
                  filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))'
                }}>delete_forever</span>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <Header
        showMessengerSidebar={false}
        onMessengerToggle={() => { }}
      />

      {/* Professional UsmaApp SHOP Header */}
      <div className="shop-header">
        <h1 className="shop-title">REZELL SHOP</h1>
        <p className="shop-subtitle">Discover, Buy, and Sell with Confidence</p>
      </div>

      {/* Professional Navigation */}
      <div className="marketplace-navigation">
        <button
          className={`nav-btn ${currentView === 'marketplace' ? 'active' : ''}`}
          onClick={() => {
            setCurrentView('marketplace');
            setActiveCategory(null);
            setSelectedSubcategory(null);
          }}
        >
          <span className="material-icons notranslate">store</span>
          Marketplace
        </button>

        {user && (
          <button
            className={`nav-btn ${currentView === 'my-products' ? 'active' : ''}`}
            onClick={() => setCurrentView('my-products')}
          >
            <span className="material-icons notranslate">inventory_2</span>
            My Products
          </button>
        )}

        <button
          className="add-product-button"
          onClick={() => setShowAddProductForm(true)}
        >
          <span className="material-icons notranslate">add_circle</span>
          Add Product
        </button>
      </div>

      {/* Marketplace View */}
      {currentView === 'marketplace' && !activeCategory ? (
        <Home
          categories={allCategories}
          showAddProductForm={showAddProductForm}
          setShowAddProductForm={setShowAddProductForm}
          newProduct={newProduct}
          setNewProduct={setNewProduct}
          handleAddProduct={handleAddProduct}
          setActiveCategory={setActiveCategory}
          handleImageChange={handleImageChange}
          handleDeleteCategory={handleDeleteCategory}
          onCategoryAdded={fetchAllCategories}
        />
      ) : currentView === 'marketplace' && activeCategory ? (
        <div className="category-section">
          <button
            className="back-button"
            onClick={() => {
              if (selectedSubcategory) {
                setSelectedSubcategory(null);
              } else {
                setActiveCategory(null);
                setSelectedSubcategory(null);
              }
            }}
          >
            <span className="material-icons notranslate">arrow_back</span>
            Back
          </button>

          <div className="category-view-header">
            <div>
              <h2 className="category-view-title">
                <span className="material-icons notranslate">category</span>
                {selectedSubcategory
                  ? `${allCategories.find(c => c.id === activeCategory || c.value === activeCategory)?.name || 'Category'} > ${selectedSubcategory.name}`
                  : (allCategories.find(c => c.id === activeCategory || c.value === activeCategory)?.name || 'Category')
                }
              </h2>
              <p className="category-breadcrumb">
                {selectedSubcategory
                  ? `${allCategories.find(c => c.id === activeCategory || c.value === activeCategory)?.name || 'Category'} / ${selectedSubcategory.name}`
                  : (allCategories.find(c => c.id === activeCategory || c.value === activeCategory)?.name || 'Category')
                }
              </p>
            </div>

            <button
              className="add-product-button"
              onClick={() => {
                // Pre-populate the form with current category and subcategory
                setNewProduct(prev => ({
                  ...prev,
                  category: activeCategory,
                  subcategory: selectedSubcategory?.value || selectedSubcategory?.name || '',
                  mobileSubCategory: activeCategory === 'mobiles' && selectedSubcategory ? (selectedSubcategory.value || selectedSubcategory.name) : ''
                }));
                setShowAddProductForm(true);
              }}
            >
              <span className="material-icons notranslate">add_circle</span>
              Add Product
            </button>
          </div>

          {/* Professional Subcategories Section */}
          {(() => {
            const currentCategory = allCategories.find(c => c.id === activeCategory || c.value === activeCategory);
            return currentCategory?.subcategories && currentCategory.subcategories.length > 0 && (
              <div className="subcategories-section">
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  marginBottom: '1rem',
                  color: 'var(--gray-900)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span className="material-icons notranslate">subdirectory_arrow_right</span>
                  Subcategories
                </h3>
                <div className="subcategories-grid">
                  {currentCategory.subcategories.map((sub, index) => (
                    <div
                      key={index}
                      className={`subcategory-item ${selectedSubcategory?.name === sub.name ? 'active' : ''}`}
                      onClick={() => setSelectedSubcategory(sub)}
                    >
                      <span className="subcategory-icon material-icons notranslate">
                        {getSubcategoryIcon(sub.name)}
                      </span>
                      <span className="subcategory-name">{sub.name}</span>
                    </div>
                  ))}

                  {/* Add new subcategory button */}
                  <button
                    className="subcategory-item add-new-subcategory"
                    onClick={() => setShowAddSubcategoryModal(true)}
                  >
                    <span className="subcategory-icon material-icons notranslate">add_circle</span>
                    <span className="subcategory-name">Add Subcategory</span>
                  </button>
                </div>
              </div>
            );
          })()}

          {showAddProductForm && (
            <div className="modal-overlay" style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1001
            }}>
              <div className="modal-content" style={{
                background: 'white',
                padding: '20px',
                borderRadius: '8px',
                width: '90%',
                maxWidth: '500px',
                position: 'relative'
              }}>
                <div className="modal-header" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  position: 'relative'
                }}>
                  <h2 className="modal-header-title" style={{
                    margin: 0,
                    fontSize: '24px',
                    fontWeight: 'normal',
                    color: '#333'
                  }}>Add New Product</h2>
                  <div className="modal-close"
                    onClick={() => {
                      setShowAddProductForm(false);
                      setNewProduct({ name: '', category: '', subcategory: '', images: [], price: '', contact: '', countryCode: '+91', description: '', mobileSubCategory: '' });
                    }}
                    style={{
                      cursor: 'pointer',
                      fontSize: '24px',
                      color: '#666',
                      position: 'relative',
                      top: 'auto',
                      right: 'auto'
                    }}
                  >×</div>
                </div>

                <input
                  placeholder="Name"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    marginBottom: '15px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '15px',
                    boxSizing: 'border-box'
                  }}
                />

                <select
                  value={newProduct.category || ''}
                  onChange={(e) => setNewProduct({ ...newProduct, category: e.target.value, subcategory: '', mobileSubCategory: '' })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    marginBottom: '15px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '15px',
                    backgroundColor: 'white',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select Category</option>
                  {allCategories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {typeof cat.name === 'string' ? cat.name : 'Home Appliances'}
                    </option>
                  ))}
                </select>

                {(() => {
                  // Check if the selected category has subcategories
                  const selectedCategory = allCategories.find(cat => cat.id === newProduct.category || cat.value === newProduct.category);
                  const hasSubcategories = selectedCategory?.subcategories && selectedCategory.subcategories.length > 0;

                  if (hasSubcategories) {
                    return (
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{
                          display: 'block',
                          marginBottom: '5px',
                          fontSize: '14px',
                          color: '#666',
                          fontWeight: '500'
                        }}>
                          Subcategory (Optional)
                        </label>
                        <select
                          value={newProduct.subcategory || newProduct.mobileSubCategory || ''}
                          onChange={e => setNewProduct({
                            ...newProduct,
                            subcategory: e.target.value,
                            mobileSubCategory: newProduct.category === 'mobiles' ? e.target.value : ''
                          })}
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '15px',
                            boxSizing: 'border-box'
                          }}
                        >
                          <option value="">Select Subcategory (Optional)</option>
                          {selectedCategory.subcategories.map(sub => (
                            <option key={sub.value || sub.name} value={sub.value || sub.name}>
                              {sub.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  }

                  // Fallback for mobile category (backward compatibility)
                  if (newProduct.category === 'mobiles') {
                    return (
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{
                          display: 'block',
                          marginBottom: '5px',
                          fontSize: '14px',
                          color: '#666',
                          fontWeight: '500'
                        }}>
                          Mobile Subcategory (Optional)
                        </label>
                        <select
                          value={newProduct.mobileSubCategory || ''}
                          onChange={e => setNewProduct({ ...newProduct, mobileSubCategory: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '12px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '15px',
                            boxSizing: 'border-box'
                          }}
                        >
                          <option value="">Select Mobile Subcategory (Optional)</option>
                          <option value="iphone">iPhone</option>
                          <option value="android">Android</option>
                        </select>
                      </div>
                    );
                  }

                  return null;
                })()}

                <div style={{
                  marginBottom: '10px',
                  padding: '8px 12px',
                  backgroundColor: newProduct.images.length < 3 ? '#fff3cd' : newProduct.images.length === 5 ? '#d1ecf1' : '#d4edda',
                  borderRadius: '6px',
                  border: `1px solid ${newProduct.images.length < 3 ? '#ffeaa7' : newProduct.images.length === 5 ? '#bee5eb' : '#c3e6cb'}`
                }}>
                  <small style={{
                    color: newProduct.images.length < 3 ? '#856404' : newProduct.images.length === 5 ? '#0c5460' : '#155724',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    📸 Images: {newProduct.images.length}/5 •
                    {newProduct.images.length < 3 ? ` Need ${3 - newProduct.images.length} more` :
                      newProduct.images.length === 5 ? ' Maximum reached' : ' Looking good!'}
                  </small>
                </div>

                <div style={{ position: 'relative', marginBottom: '15px' }}>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={e => {
                      const currentImages = newProduct.images || [];
                      const files = Array.from(e.target.files);
                      const totalImages = currentImages.length + files.length;

                      if (totalImages > 5) {
                        alert('You can upload maximum 5 images per product');
                        return;
                      }

                      Promise.all(files.map(file => {
                        return new Promise((resolve) => {
                          const reader = new FileReader();
                          reader.onload = (e) => resolve(e.target.result);
                          reader.readAsDataURL(file);
                        });
                      })).then(images => {
                        setNewProduct(prev => ({
                          ...prev,
                          images: [...currentImages, ...images].slice(0, 5)
                        }));
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '15px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                {newProduct.images.length > 0 && (
                  <div style={{
                    display: 'flex',
                    gap: '10px',
                    marginBottom: '15px',
                    flexWrap: 'wrap'
                  }}>
                    {newProduct.images.map((img, idx) => (
                      <div key={idx} style={{ position: 'relative' }}>
                        <img
                          src={img}
                          alt={`Preview ${idx + 1}`}
                          style={{
                            width: '80px',
                            height: '80px',
                            objectFit: 'cover',
                            borderRadius: '4px'
                          }}
                        />
                        <button
                          onClick={() => {
                            const newImages = newProduct.images.filter((_, i) => i !== idx);
                            setNewProduct({ ...newProduct, images: newImages });
                          }}
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            background: '#ff4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '14px',
                            padding: 0
                          }}
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}

                <input
                  type="number"
                  placeholder="Price"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value.replace(/[^\d]/g, '') })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    marginBottom: '15px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '15px',
                    boxSizing: 'border-box'
                  }}
                />

                {/* Contact Number with Country Code (custom searchable dropdown) */}
                {(() => {
                  const selectedCountry = getCountryByCode(newProduct.countryCode);
                  const phoneInvalid =
                    !!newProduct.contact && !isContactValid(newProduct.contact, selectedCountry);
                  const fieldHeight = 44;
                  const borderColor = phoneInvalid ? '#ff4444' : '#ccc';

                  const handleSelectCountry = (newCode) => {
                    const newCountry = getCountryByCode(newCode);
                    const trimmedContact = (newProduct.contact || '').slice(0, newCountry.max);
                    setNewProduct({
                      ...newProduct,
                      countryCode: newCode,
                      contact: trimmedContact,
                    });
                    setCountryDropdownOpen(false);
                    setCountrySearch('');
                  };

                  const search = countrySearch.trim().toLowerCase();
                  const filteredCountries = search
                    ? COUNTRY_CODES.filter(
                        (c) =>
                          c.name.toLowerCase().includes(search) ||
                          c.code.replace('+', '').includes(search.replace('+', ''))
                      )
                    : COUNTRY_CODES;

                  return (
                    <div style={{ marginBottom: '15px' }}>
                      <label
                        htmlFor="product-contact-number"
                        style={{
                          display: 'block',
                          marginBottom: '6px',
                          fontSize: '14px',
                          color: '#666',
                          fontWeight: '500',
                        }}
                      >
                        Contact Number
                      </label>

                      {/* The wrapper is the positioning anchor for the popover so
                          it can use `right: 0` to never overflow the form width. */}
                      <div
                        ref={countryDropdownRef}
                        style={{
                          position: 'relative',
                          width: '100%',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'stretch',
                            gap: '8px',
                            width: '100%',
                          }}
                        >
                          {/* Country code trigger button — same compact 95px size as before */}
                          <button
                            type="button"
                            aria-label="Select country code"
                            aria-haspopup="listbox"
                            aria-expanded={countryDropdownOpen}
                            onClick={() => setCountryDropdownOpen((o) => !o)}
                            style={{
                              width: '95px',
                              flex: '0 0 95px',
                              height: `${fieldHeight}px`,
                              padding: '0 10px',
                              border: `1px solid ${countryDropdownOpen ? '#673ab7' : borderColor}`,
                              borderRadius: '4px',
                              fontSize: '15px',
                              color: '#333',
                              backgroundColor: 'white',
                              boxSizing: 'border-box',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '4px',
                              WebkitAppearance: 'none',
                              appearance: 'none',
                              outline: 'none',
                            }}
                          >
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                            }}>
                              <span style={{ fontSize: '16px', lineHeight: 1 }}>{selectedCountry.flag}</span>
                              <span>{selectedCountry.code}</span>
                            </span>
                            <span
                              aria-hidden="true"
                              style={{
                                display: 'inline-block',
                                width: 0,
                                height: 0,
                                borderLeft: '4px solid transparent',
                                borderRight: '4px solid transparent',
                                borderTop: '5px solid #666',
                                transform: countryDropdownOpen ? 'rotate(180deg)' : 'none',
                                transition: 'transform 120ms ease',
                                flexShrink: 0,
                              }}
                            />
                          </button>

                          <input
                            id="product-contact-number"
                            type="tel"
                            inputMode="numeric"
                            autoComplete="tel-national"
                            pattern="[0-9]*"
                            placeholder={`Enter ${getExpectedDigitsLabel(selectedCountry)}`}
                            value={newProduct.contact}
                            onChange={(e) => {
                              const digits = e.target.value.replace(/\D/g, '');
                              if (digits.length <= selectedCountry.max) {
                                setNewProduct({ ...newProduct, contact: digits });
                              }
                            }}
                            maxLength={selectedCountry.max}
                            style={{
                              flex: '1 1 auto',
                              minWidth: 0,
                              width: '100%',
                              height: `${fieldHeight}px`,
                              lineHeight: `${fieldHeight - 2}px`,
                              padding: '0 12px',
                              border: `1px solid ${borderColor}`,
                              borderRadius: '4px',
                              // 16px prevents iOS Safari from auto-zooming on focus.
                              fontSize: '16px',
                              color: '#333',
                              backgroundColor: 'white',
                              boxSizing: 'border-box',
                              WebkitAppearance: 'none',
                              appearance: 'none',
                            }}
                          />
                        </div>

                        {countryDropdownOpen && (
                          <div
                            role="listbox"
                            aria-label="Country code"
                            style={{
                              position: 'absolute',
                              top: 'calc(100% + 6px)',
                              left: 0,
                              // `right: 0` together with `maxWidth: 280px` lets the
                              // popover stay 240–280px on desktop but shrink to the
                              // form width on mobile so it never overflows.
                              right: 'auto',
                              width: 'min(280px, 100%)',
                              maxWidth: '100%',
                              maxHeight: 'min(300px, 60vh)',
                              background: 'white',
                              border: '1px solid #ddd',
                              borderRadius: '6px',
                              boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
                              display: 'flex',
                              flexDirection: 'column',
                              overflow: 'hidden',
                              zIndex: 1100,
                            }}
                          >
                            <div style={{
                              padding: '8px',
                              borderBottom: '1px solid #eee',
                              backgroundColor: '#fafafa',
                            }}>
                              <input
                                ref={countrySearchInputRef}
                                type="text"
                                value={countrySearch}
                                onChange={(e) => setCountrySearch(e.target.value)}
                                placeholder="Search country or code"
                                aria-label="Search country"
                                style={{
                                  width: '100%',
                                  height: '36px',
                                  padding: '0 10px',
                                  border: '1px solid #d0d0d0',
                                  borderRadius: '4px',
                                  fontSize: '14px',
                                  boxSizing: 'border-box',
                                  outline: 'none',
                                  WebkitAppearance: 'none',
                                  appearance: 'none',
                                }}
                              />
                            </div>

                            <div
                              style={{
                                overflowY: 'auto',
                                WebkitOverflowScrolling: 'touch',
                                flex: '1 1 auto',
                                maxHeight: '240px',
                              }}
                            >
                              {filteredCountries.length === 0 ? (
                                <div style={{
                                  padding: '14px 12px',
                                  color: '#888',
                                  fontSize: '14px',
                                  textAlign: 'center',
                                }}>
                                  No countries match "{countrySearch}"
                                </div>
                              ) : (
                                filteredCountries.map((c) => {
                                  const isActive = c.code === newProduct.countryCode;
                                  return (
                                    <button
                                      key={c.code}
                                      type="button"
                                      role="option"
                                      aria-selected={isActive}
                                      onClick={() => handleSelectCountry(c.code)}
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: 'none',
                                        borderBottom: '1px solid #f3f3f3',
                                        background: isActive ? '#ede7f6' : 'white',
                                        color: '#333',
                                        fontSize: '14px',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        outline: 'none',
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isActive) e.currentTarget.style.background = '#f5f5f5';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.background = isActive ? '#ede7f6' : 'white';
                                      }}
                                    >
                                      <span style={{ fontSize: '18px', lineHeight: 1, width: '24px', textAlign: 'center' }}>
                                        {c.flag}
                                      </span>
                                      <span style={{
                                        flex: '1 1 auto',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        fontWeight: isActive ? 600 : 400,
                                      }}>
                                        {c.name}
                                      </span>
                                      <span style={{
                                        color: '#777',
                                        fontVariantNumeric: 'tabular-nums',
                                        flexShrink: 0,
                                      }}>
                                        {c.code}
                                      </span>
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {phoneInvalid && (
                        <div
                          style={{
                            color: '#ff4444',
                            fontSize: '12px',
                            marginTop: '6px',
                          }}
                        >
                          Phone number must be {getExpectedDigitsLabel(selectedCountry)}{' '}
                          for {selectedCountry.name} ({selectedCountry.code}).
                        </div>
                      )}
                    </div>
                  );
                })()}

                <textarea
                  placeholder="Product Description"
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    marginBottom: '20px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '15px',
                    minHeight: '100px',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />

                <button
                  onClick={handleAddProduct}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: 'rgb(103, 58, 183)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '16px',
                    cursor: 'pointer'
                  }}
                >
                  Submit
                </button>
              </div>
            </div>
          )}

          {/* Products section */}
          <div className="products">
            {filteredProducts.length > 0 ? (
              filteredProducts.map((product, index) => (
                <ProductCard
                  key={product._id || index}
                  index={index}
                  product={product}
                  onDelete={handleDeleteProduct}
                  category={activeCategory}
                  subCategory={selectedSubcategory ? (selectedSubcategory.value || selectedSubcategory.name) : null}
                  currentUser={user}
                  onStartConversation={startConversationWithProductContext}
                  onMarkAsSold={handleMarkAsSold}
                  onImageClick={handleImageClick}
                  onEditSuccess={handleEditSuccess}
                />
              ))
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                color: '#666',
                fontSize: '16px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '15px'
              }}>
                <div style={{
                  fontSize: '64px',
                  color: '#ddd',
                  marginBottom: '15px'
                }}>📦</div>
                <p style={{ margin: 0, fontSize: '22px', fontWeight: '600', color: '#4a5568' }}>
                  {selectedSubcategory
                    ? `No products found in "${selectedSubcategory.name}"`
                    : `No products found in this category`
                  }
                </p>
                <p style={{ margin: 0, fontSize: '16px', color: '#718096' }}>
                  Be the first to add a product {selectedSubcategory ? 'in this subcategory' : 'here'}!
                </p>
                <button
                  onClick={() => {
                    setNewProduct(prev => ({
                      ...prev,
                      category: activeCategory,
                      subcategory: selectedSubcategory?.value || selectedSubcategory?.name || ''
                    }));
                    setShowAddProductForm(true);
                  }}
                  style={{
                    marginTop: '20px',
                    padding: '12px 24px',
                    backgroundColor: '#4299e1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 4px rgba(66, 153, 225, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#3182ce';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 8px rgba(66, 153, 225, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#4299e1';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 4px rgba(66, 153, 225, 0.3)';
                  }}
                >
                  + Add First Product
                </button>
              </div>
            )}
          </div>

          {/* Add Subcategory Modal */}
          {showAddSubcategoryModal && (
            <div className="modal-overlay" style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1002
            }}>
              <div className="modal-content" style={{
                background: 'white',
                padding: '25px',
                borderRadius: '12px',
                width: '90%',
                maxWidth: '400px',
                position: 'relative',
                boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
              }}>
                <div className="modal-close"
                  onClick={() => {
                    setShowAddSubcategoryModal(false);
                    setNewSubcategoryName('');
                  }}
                  style={{
                    position: 'absolute',
                    right: '15px',
                    top: '15px',
                    cursor: 'pointer',
                    fontSize: '24px',
                    color: '#666',
                    fontWeight: 'bold'
                  }}
                >×</div>

                <h2 style={{
                  margin: '0 0 15px 0',
                  fontSize: '22px',
                  fontWeight: '600',
                  color: '#333'
                }}>Add Subcategory</h2>

                <p style={{
                  margin: '0 0 20px 0',
                  color: '#666',
                  fontSize: '14px'
                }}>
                  Adding subcategory to: <strong>
                    {allCategories.find(c => c.id === activeCategory || c.value === activeCategory)?.name || 'Category'}
                  </strong>
                </p>

                <input
                  placeholder="Enter subcategory name"
                  value={newSubcategoryName}
                  onChange={(e) => setNewSubcategoryName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    marginBottom: '20px',
                    borderRadius: '8px',
                    border: '2px solid #e2e8f0',
                    fontSize: '15px',
                    boxSizing: 'border-box',
                    outline: 'none',
                    transition: 'border-color 0.2s ease'
                  }}
                  autoFocus
                  onFocus={(e) => e.target.style.borderColor = '#42a5f5'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />

                <div style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    onClick={() => {
                      setShowAddSubcategoryModal(false);
                      setNewSubcategoryName('');
                    }}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      backgroundColor: '#e53e3e',
                      color: 'white',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#c53030'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#e53e3e'}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddSubcategory}
                    disabled={!newSubcategoryName.trim()}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      backgroundColor: !newSubcategoryName.trim() ? '#a0aec0' : '#38a169',
                      color: 'white',
                      border: 'none',
                      cursor: !newSubcategoryName.trim() ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (newSubcategoryName.trim()) {
                        e.target.style.backgroundColor = '#2f855a';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (newSubcategoryName.trim()) {
                        e.target.style.backgroundColor = '#38a169';
                      }
                    }}
                  >
                    Add Subcategory
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : currentView === 'my-products' ? (
        /* My Products View */
        <div className="my-products-view">
          <div className="my-products-header">
            <h2 className="my-products-title">
              My Products ({myProductsView === 'available' ? userProducts.length : soldProducts.length})
            </h2>

            {/* Navigation between Available and Sold products */}
            <div className="my-products-navigation" style={{
              display: 'flex',
              gap: '10px',
              marginTop: '15px',
              justifyContent: 'flex-end', // Move buttons to right
              width: '100%'
            }}>
              <button
                className={`products-nav-btn ${myProductsView === 'available' ? 'active' : ''}`}
                onClick={() => setMyProductsView('available')}
                style={{
                  padding: '10px 20px',
                  borderRadius: '20px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: myProductsView === 'available'
                    ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
                    : '#ffffff',
                  color: myProductsView === 'available' ? '#ffffff' : '#495057',
                  boxShadow: myProductsView === 'available'
                    ? '0 4px 15px rgba(40, 167, 69, 0.3)'
                    : '0 2px 10px rgba(0, 0, 0, 0.1)'
                }}
              >
                📦 Available Products
              </button>

              <button
                className={`products-nav-btn ${myProductsView === 'sold' ? 'active' : ''}`}
                onClick={() => setMyProductsView('sold')}
                style={{
                  padding: '10px 20px',
                  borderRadius: '20px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  background: myProductsView === 'sold'
                    ? 'linear-gradient(135deg, #6c757d 0%, #495057 100%)'
                    : '#ffffff',
                  color: myProductsView === 'sold' ? '#ffffff' : '#495057',
                  boxShadow: myProductsView === 'sold'
                    ? '0 4px 15px rgba(108, 117, 125, 0.3)'
                    : '0 2px 10px rgba(0, 0, 0, 0.1)'
                }}
              >
                ✅ Sold Items
              </button>
            </div>
          </div>

          {/* Show products based on current view */}
          {myProductsView === 'available' ? (
            userProducts.length > 0 ? (
              <div>
                <div className="products-grid">
                  {userProducts.map((product, index) => (
                    <div key={product._id} className="product-item-container">
                      <ProductCard
                        product={product}
                        onDelete={handleDeleteProduct}
                        index={index}
                        currentUser={user}
                        onStartConversation={startConversationWithProductContext}
                        onMarkAsSold={handleMarkAsSold}
                        onImageClick={handleImageClick}
                        onEditSuccess={handleEditSuccess}
                      />
                      {/* Interest notifications for product owners */}
                      {product.interests && product.interests.length > 0 && (
                        <div className="interest-notifications">
                          <h4 className="interest-notifications-title">
                            <span className="material-icons notranslate" style={{ fontSize: '16px' }}>favorite</span>
                            Interested Buyers ({product.interests.length}):
                          </h4>
                          {product.interests.map((interest, idx) => (
                            <div key={idx} className="interest-item">
                              <img
                                src={interest.user.picture}
                                alt={interest.user.name}
                                className="interest-avatar"
                              />
                              <div className="interest-user-info">
                                <div className="interest-user-name">
                                  {interest.user.first_name} {interest.user.last_name}
                                </div>
                                <div className="interest-date">
                                  Interested On Your Product {new Date(interest.interestedAt).toLocaleDateString()}
                                </div>
                              </div>
                              <button
                                onClick={() => startConversationWithProductContext(interest.user._id, product)}
                                className="chat-btn"
                                title={`Message ${interest.user.first_name} about this product`}
                              >
                                <span className="material-icons notranslate" style={{ fontSize: '14px' }}>message</span>
                                Chat
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="empty-products">
                <div className="empty-products-icon">📦</div>
                <h3>No Available Products</h3>
                <p>
                  You don't have any available products. You can add products from the marketplace section.
                </p>
              </div>
            )
          ) : (
            /* Sold Products View */
            soldProducts.length > 0 ? (
              <div>
                <div className="products-grid">
                  {soldProducts.map((product, index) => (
                    <div key={product._id} className="product-item-container sold-product">
                      <ProductCard
                        product={product}
                        onDelete={handleDeleteProduct}
                        index={index}
                        currentUser={user}
                        onStartConversation={startConversationWithProductContext}
                        onMarkAsSold={handleMarkAsSold}
                        onImageClick={handleImageClick}
                        onEditSuccess={handleEditSuccess}
                      />

                      {/* The "SOLD" badge itself is rendered inside ProductCard so
                          there's only one badge per card. Here we just show when
                          the product was sold, below the card. */}
                      {product.soldAt && (
                        <div className="sold-date" style={{
                          padding: '10px',
                          background: '#f8f9fa',
                          borderTop: '1px solid #dee2e6',
                          fontSize: '12px',
                          color: '#666',
                          textAlign: 'center'
                        }}>
                          Sold on {new Date(product.soldAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="empty-products">
                <div className="empty-products-icon">✅</div>
                <h3>No Sold Items</h3>
                <p>
                  You haven't sold any products yet. Mark your products as sold when they're purchased!
                </p>
              </div>
            )
          )}
        </div>
      ) : null}

      {/* Image Modal */}
      {showImageModal && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Close button */}
            <button className="image-modal-close" onClick={closeImageModal}>
              <span className="material-icons notranslate">close</span>
            </button>

            {/* Product name */}
            <div className="image-modal-header">
              <h3>{modalProductName}</h3>
              <p>Image {modalCurrentIndex + 1} of {modalImages.length}</p>
            </div>

            {/* Main image — pinch / double-tap zoom, pan, swipe when not zoomed */}
            <ZoomableImageViewer
              src={modalImages[modalCurrentIndex]}
              alt={`${modalProductName} - ${modalCurrentIndex + 1}`}
              imageKey={modalCurrentIndex}
              canSwipe={modalImages.length > 1}
              onSwipeLeft={nextModalImage}
              onSwipeRight={prevModalImage}
            >
              {({ isZoomed }) => (
                modalImages.length > 1 && !isZoomed ? (
                  <>
                    <button
                      type="button"
                      className="image-modal-nav image-modal-prev"
                      onClick={prevModalImage}
                      aria-label="Previous image"
                    >
                      <span className="material-icons notranslate">chevron_left</span>
                    </button>
                    <button
                      type="button"
                      className="image-modal-nav image-modal-next"
                      onClick={nextModalImage}
                      aria-label="Next image"
                    >
                      <span className="material-icons notranslate">chevron_right</span>
                    </button>
                  </>
                ) : null
              )}
            </ZoomableImageViewer>

            {/* Thumbnail navigation */}
            {modalImages.length > 1 && (
              <div className="image-modal-thumbnails">
                {modalImages.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className={`image-modal-thumbnail ${index === modalCurrentIndex ? 'active' : ''}`}
                    onClick={() => setModalCurrentIndex(index)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
